import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GoogleMap, useJsApiLoader, Marker as GMarker, InfoWindow } from '@react-google-maps/api';
import ReactApexChart from 'react-apexcharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import robotoFont from '../utils/robotoFont';
import * as XLSX from 'xlsx';
import { sevkiyatService, cihazBilgiService } from '../api/dbService';
import PageLayout from '../components/PageLayout';
import LoadingSpinner from '../components/LoadingSpinner';


const LIMIT_OPTIONS = [
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
  { label: '250', value: 250 },
  { label: 'Tümü', value: 2000 }
];

export default function ShipmentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sevkiyat, setSevkiyat] = useState(null);
  const [sensor, setSensor] = useState(null);
  const [tarih, setTarih] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(100);
  const [tamamlaModal, setTamamlaModal] = useState(false);
  const [tamamlaNot, setTamamlaNot] = useState('');
  const [completing, setCompleting] = useState(false);
  const [activeMarker, setActiveMarker] = useState(null);
  const [locationCache, setLocationCache] = useState({});
  const fetchingRef = useRef(new Set());

  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  });

  const fetchSensor = useCallback(async (device_id, lim) => {
    try {
      const [sonRes, tarihRes] = await Promise.all([
        cihazBilgiService.getSonVeri(device_id),
        cihazBilgiService.getTarihVeri(device_id, { limit: lim })
      ]);
      setSensor(sonRes.data);
      setTarih(tarihRes.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    sevkiyatService.getSevkiyatById(id)
      .then(res => {
        setSevkiyat(res.data);
        if (res.data?.device_id) fetchSensor(res.data.device_id, limit);
      })
      .catch(() => toast.error('Sevkiyat bulunamadı'))
      .finally(() => setLoading(false));
  }, [id, fetchSensor]);

  useEffect(() => {
    if (!sevkiyat?.device_id) return;
    fetchSensor(sevkiyat.device_id, limit);
  }, [limit]);

  useEffect(() => {
    if (!sevkiyat?.device_id || sevkiyat?.tamamlandi_mi) return;
    const timer = setInterval(() => fetchSensor(sevkiyat.device_id, limit), 30000);
    return () => clearInterval(timer);
  }, [sevkiyat, limit, fetchSensor]);

  useEffect(() => {
    if (!tarih.length) return;
    const unique = [...new Map(tarih.filter(r => r.enlem && r.boylam).map(r => [`${r.enlem},${r.boylam}`, r])).values()];
    unique.forEach(async (r) => {
      const key = `${r.enlem},${r.boylam}`;
      if (locationCache[key] || fetchingRef.current.has(key)) return;
      fetchingRef.current.add(key);
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${r.enlem}&lon=${r.boylam}&format=json&accept-language=tr`);
        const data = await res.json();
        const ilce = data.address?.county || data.address?.suburb || data.address?.district || '';
        const il = data.address?.city || data.address?.province || data.address?.state || '';
        setLocationCache(prev => ({ ...prev, [key]: ilce && il ? `${ilce}/${il}` : il || ilce || '—' }));
      } catch {
        setLocationCache(prev => ({ ...prev, [key]: '—' }));
      }
    });
  }, [tarih]);

  async function handleTamamla() {
    setCompleting(true);
    try {
      await sevkiyatService.completeSevkiyat(id, tamamlaNot);
      toast.success('Sevkiyat tamamlandı');
      navigate('/');
    } catch {
      toast.error('İşlem başarısız');
    } finally {
      setCompleting(false);
      setTamamlaModal(false);
    }
  }

  function exportExcel() {
    if (!tarih.length) return toast.warning('Dışa aktarılacak veri yok');
    const ws = XLSX.utils.json_to_sheet(tarih.map((r, i) => ({
      '#': tarih.length - i,
      Zaman: new Date(r.zaman_damgasi).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }),
      'Sicaklik (°C)': r.sicaklik != null ? parseFloat(Number(r.sicaklik).toFixed(1)) : null,
      'Nem (%RH)': r.nem != null ? parseFloat(Number(r.nem).toFixed(1)) : null,
      'Isik (lux)': r.isik,
      Enlem: r.enlem,
      Boylam: r.boylam,
      'Pil (%)': r.voltaj != null ? Math.round(r.voltaj) : null
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sensör Verisi');
    XLSX.writeFile(wb, `${sevkiyat.sevkiyat_no}_sensor.xlsx`);
  }

  async function exportPDF() {
    if (!tarih.length) return toast.warning('Dışa aktarılacak veri yok');

    async function imgToDataUrl(src) {
      try {
        const res = await fetch(src);
        const blob = await res.blob();
        return await new Promise(r => { const fr = new FileReader(); fr.onloadend = () => r(fr.result); fr.readAsDataURL(blob); });
      } catch { return null; }
    }

    const [elitechImg, inkatechImg] = await Promise.all([
      imgToDataUrl('/Elitechlogo.png'),
      imgToDataUrl('/inkatechlogo.png')
    ]);

    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();

    // Logo bar arka planı
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageW, 18, 'F');

    if (elitechImg) doc.addImage(elitechImg, 'PNG', 8, 2, 36, 14);
    if (inkatechImg) doc.addImage(inkatechImg, 'PNG', pageW - 46, 2, 38, 14);

    doc.addFileToVFS('Roboto-Regular.ttf', robotoFont);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.setFont('Roboto');

    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    doc.text(`Sevkiyat: ${sevkiyat.sevkiyat_adi}`, 14, 28);
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text(`No: ${sevkiyat.sevkiyat_no}  |  Cihaz: ${sevkiyat.device_id}`, 14, 35);
    doc.text(`Güzergah: ${sevkiyat.cikis_noktasi} - ${sevkiyat.varis_noktasi}`, 14, 41);
    doc.text(`Oluşturma: ${new Date(sevkiyat.olusturma_tarihi).toLocaleString('tr-TR')}`, 14, 47);

    autoTable(doc, {
      startY: 53,
      head: [['#', 'Zaman', 'Sıcaklık (°C)', 'Nem (%RH)', 'Pil (%)', 'Enlem', 'Boylam']],
      body: tarih.map((r, i) => [
        tarih.length - i,
        new Date(r.zaman_damgasi).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }),
        r.sicaklik != null ? Number(r.sicaklik).toFixed(1) : '—',
        r.nem != null ? Number(r.nem).toFixed(1) : '—',
        r.voltaj != null ? Math.round(r.voltaj) : '—',
        r.enlem ?? '—', r.boylam ?? '—'
      ]),
      styles: { fontSize: 8, font: 'Roboto' },
      headStyles: { fillColor: [14, 165, 233], font: 'Roboto' }
    });
    doc.save(`${sevkiyat.sevkiyat_no}_sensor.pdf`);
  }

  if (loading) return <PageLayout><LoadingSpinner /></PageLayout>;
  if (!sevkiyat) return <PageLayout><div className="alert alert-warning">Sevkiyat bulunamadı</div></PageLayout>;

  const hasLocation = sensor?.enlem && sensor?.boylam;

  // Çoklu marker: tarih DESC sıralı → kronolojik sıraya çevir
  const validTarih = [...tarih].filter(r => r.enlem && r.boylam).reverse();
  const markerGroups = new Map();
  validTarih.forEach((r, i) => {
    const key = `${Number(r.enlem).toFixed(5)},${Number(r.boylam).toFixed(5)}`;
    if (!markerGroups.has(key)) markerGroups.set(key, { lat: parseFloat(r.enlem), lon: parseFloat(r.boylam), count: 0, indices: [] });
    const g = markerGroups.get(key);
    g.count++;
    g.indices.push(i);
  });
  const total = validTarih.length;
  const markerList = Array.from(markerGroups.values()).map(g => {
    const isFirst = g.indices.includes(0);
    const isLast  = g.indices.includes(total - 1);
    const color = isLast ? '#22c55e' : isFirst ? '#3b82f6' : '#ef4444';
    return { ...g, color };
  });

  // Chart
  const chartCategories = [...tarih].reverse().map(r => new Date(r.zaman_damgasi).getTime());
  const sicaklikSeries = [...tarih].reverse().map(r => r.sicaklik ?? null);
  const nemSeries = [...tarih].reverse().map(r => r.nem ?? null);

  const chartOptions = {
    chart: { type: 'area', height: 260, toolbar: { show: true }, zoom: { enabled: true }, animations: { enabled: false } },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: [2, 2] },
    colors: ['#0ea5e9', '#10b981'],
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.25, opacityTo: 0.02, stops: [0, 100] }
    },
    xaxis: { type: 'datetime', categories: chartCategories, labels: { datetimeUTC: false } },
    yaxis: [
      { title: { text: '°C', style: { color: '#0ea5e9' } }, labels: { style: { colors: '#0ea5e9' } } },
      { opposite: true, title: { text: '%', style: { color: '#10b981' } }, labels: { style: { colors: '#10b981' } } }
    ],
    tooltip: { x: { format: 'dd MMM HH:mm' }, shared: true },
    legend: { position: 'top' },
    grid: { borderColor: 'rgba(14,165,233,.1)' }
  };

  const chartSeries = [
    { name: 'Sıcaklık (°C)', data: sicaklikSeries },
    { name: 'Nem (%)', data: nemSeries }
  ];

  return (
    <PageLayout>
      {/* Başlık */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 className="mb-0 fw-semibold">{sevkiyat.sevkiyat_adi}</h5>
        <div className="d-flex gap-2 flex-wrap">
          {!sevkiyat.tamamlandi_mi && (
            <button className="btn btn-success btn-sm" onClick={() => setTamamlaModal(true)}>
              Sevkiyatı Tamamla
            </button>
          )}
          {sevkiyat.tamamlandi_mi && (
            <span className="badge bg-success d-flex align-items-center px-3">✓ Tamamlandı</span>
          )}
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>Geri</button>
        </div>
      </div>

      {/* Sensör Kartları */}
      <div className="row g-3 mb-3">
        {[
          { label: 'Sıcaklık', value: sensor?.sicaklik != null ? `${Number(sensor.sicaklik).toFixed(1)}°C` : '—', icon: 'fa-thermometer-half' },
          { label: 'Nem', value: sensor?.nem != null ? `${Number(sensor.nem).toFixed(1)} %RH` : '—', icon: 'fa-tint' },
          { label: 'Işık', value: sensor?.isik != null ? `${sensor.isik} lux` : '—', icon: 'fa-sun' },
          { label: 'Pil', value: sensor?.voltaj != null ? `${Math.round(sensor.voltaj)}%` : '—', icon: 'fa-battery-half' }
        ].map(item => (
          <div key={item.label} className="col-6 col-md-3">
            <div className="card sensor-card p-3 text-center">
              <i className={`fas ${item.icon} fa-2x mb-2`} style={{ color: 'var(--teal)' }}></i>
              <div className="fw-bold fs-5">{item.value}</div>
              <small className="text-muted">{item.label}</small>
            </div>
          </div>
        ))}
      </div>

      {/* Sevkiyat Detayları */}
      <div className="card mb-3">
        <div className="card-header py-2"><small className="fw-semibold">Sevkiyat Detayları</small></div>
        <div className="card-body">
          <div className="row g-2 small">
            {[
              ['Sevkiyat No', sevkiyat.sevkiyat_no],
              ['Cihaz Kodu', sevkiyat.device_id],
              ['Plaka', sevkiyat.plaka],
              ['Güzergah', `${sevkiyat.cikis_noktasi} → ${sevkiyat.varis_noktasi}`],
              ['Sürücü', `${sevkiyat.surucu_adi} / ${sevkiyat.surucu_tel}`],
              ['Ürün', sevkiyat.urun],
              ['Sıc. Aralığı', `${sevkiyat.sicaklik_min}°C — ${sevkiyat.sicaklik_maks}°C`],
              ['Oluşturma', new Date(sevkiyat.olusturma_tarihi).toLocaleString('tr-TR')]
            ].map(([k, v]) => (
              <div key={k} className="col-12 col-sm-6 col-lg-4">
                <span className="text-muted">{k}:</span> <span className="fw-medium">{v || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Harita */}
      {(markerList.length > 0 || hasLocation) && (
        <div className="card mb-3">
          <div className="card-header py-2 d-flex align-items-center gap-3">
            <small className="fw-semibold">Konum</small>
            <span className="d-flex align-items-center gap-2" style={{ fontSize: '.72rem' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} /> Başlangıç
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} /> Ara
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} /> Son Konum
            </span>
          </div>
          {mapsLoaded ? (
            <GoogleMap
              mapContainerStyle={{ height: 320, width: '100%' }}
              center={markerList.length > 0
                ? { lat: markerList[0].lat, lng: markerList[0].lon }
                : { lat: sensor.enlem, lng: sensor.boylam }}
              zoom={13}
              options={{ scrollwheel: true, gestureHandling: 'greedy' }}
              onLoad={map => {
                if (markerList.length > 1) {
                  const bounds = new window.google.maps.LatLngBounds();
                  markerList.forEach(m => bounds.extend({ lat: m.lat, lng: m.lon }));
                  map.fitBounds(bounds);
                }
              }}
            >
              {markerList.length > 0 ? markerList.map((m, i) => (
                <GMarker
                  key={i}
                  position={{ lat: m.lat, lng: m.lon }}
                  label={m.count > 1 ? { text: String(m.count), color: 'white', fontSize: '11px', fontWeight: 'bold' } : undefined}
                  onClick={() => setActiveMarker(activeMarker === i ? null : i)}
                >
                  {activeMarker === i && (
                    <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                      <div>
                        <strong>{m.count > 1 ? `${m.count} ölçüm` : '1 ölçüm'}</strong><br />
                        {m.lat.toFixed(5)}, {m.lon.toFixed(5)}
                      </div>
                    </InfoWindow>
                  )}
                </GMarker>
              )) : (
                <GMarker position={{ lat: sensor.enlem, lng: sensor.boylam }} />
              )}
            </GoogleMap>
          ) : (
            <div style={{ height: 320 }} className="d-flex align-items-center justify-content-center text-muted">
              <small>Harita yükleniyor...</small>
            </div>
          )}
        </div>
      )}

      {/* Grafik */}
      {tarih.length > 0 && (
        <div className="card mb-3">
          <div className="card-header py-2">
            <small className="fw-semibold">Sıcaklık & Nem Grafiği</small>
          </div>
          <div className="card-body p-2">
            <ReactApexChart options={chartOptions} series={chartSeries} type="area" height={260} />
          </div>
        </div>
      )}

      {/* Geçmiş Tablo */}
      <div className="card">
        <div className="card-header py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <small className="fw-semibold">Sensör Geçmişi ({tarih.length} kayıt)</small>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* Limit Seçici */}
            <div className="btn-group btn-group-sm" role="group">
              {LIMIT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`btn ${limit === opt.value ? 'btn-brand' : 'btn-outline-secondary'}`}
                  onClick={() => setLimit(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {/* Export */}
            <button className="btn btn-outline-success btn-sm" onClick={exportExcel}>
              <i className="fas fa-file-excel me-1"></i>Excel
            </button>
            <button className="btn btn-outline-danger btn-sm" onClick={exportPDF}>
              <i className="fas fa-file-pdf me-1"></i>PDF
            </button>
          </div>
        </div>
        {tarih.length > 0 ? (
          <div className="table-responsive" style={{ maxHeight: 320 }}>
            <table className="table table-sm table-hover mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th>Zaman</th><th>Sıcaklık</th><th>Nem</th><th>Pil</th><th>Konum</th>
                </tr>
              </thead>
              <tbody>
                {tarih.map((r, i) => (
                  <tr key={i}>
                    <td><small className="text-muted">{tarih.length - i}</small></td>
                    <td><small>{new Date(r.zaman_damgasi).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</small></td>
                    <td>{r.sicaklik != null ? `${Number(r.sicaklik).toFixed(1)}°C` : '—'}</td>
                    <td>{r.nem != null ? `${Number(r.nem).toFixed(1)} %RH` : '—'}</td>
                    <td>{r.voltaj != null ? `${Math.round(r.voltaj)}%` : '—'}</td>
                    <td><small>{r.enlem && r.boylam ? (locationCache[`${r.enlem},${r.boylam}`] ?? '...') : '—'}</small></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-muted py-4 small">Henüz sensör verisi yok</div>
        )}
      </div>

      {/* Tamamlama Modal */}
      {tamamlaModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Sevkiyatı Tamamla</h6>
                <button className="btn-close" onClick={() => setTamamlaModal(false)} />
              </div>
              <div className="modal-body">
                <label className="form-label small">Not (opsiyonel)</label>
                <textarea className="form-control" rows="3" value={tamamlaNot} onChange={e => setTamamlaNot(e.target.value)} />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setTamamlaModal(false)}>İptal</button>
                <button className="btn btn-success btn-sm" onClick={handleTamamla} disabled={completing}>
                  {completing ? 'İşleniyor...' : 'Tamamla'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
